<template v-editable="props.blok">
    <div
        ref="parallaxWrapperRef"
        class="top-story"
        :class="['--theme-' + props.blok.theme, props.blok.mediaRight ? '--media-right' : '']"
    >
        <div class="max-width-wrapper">
            <div
                ref="imageWrapperRef"
                class="image-wrapper"
            >
                <svg
                    aria-hidden="true"
                    viewBox="0 0 1 1"
                    style="fill-rule: evenodd; clip-rule: evenodd; stroke-linejoin: round; stroke-miterlimit: 2"
                >
                    <defs>
                        <clipPath
                            id="top-story-mask"
                            clipPathUnits="objectBoundingBox"
                        >
                            <circle
                                cx="0.5"
                                cy="0.5"
                                r="0.5"
                            />
                        </clipPath>
                    </defs>
                </svg>

                <!-- Pink Gradient Shadow -->
                <svg
                    class="image-gradient-pink"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink"
                    viewBox="0 0 200 200"
                    aria-hidden="true"
                    focusable="false"
                >
                    <defs>
                        <linearGradient id="topstory-pink-a">
                            <stop
                                offset="0"
                                style="stop-color: #e5c6e1; stop-opacity: 1"
                            />
                            <stop
                                offset="1"
                                style="stop-color: #e5c6e1; stop-opacity: 0"
                            />
                        </linearGradient>
                        <linearGradient
                            id="topstory-pink-b"
                            xlink:href="#topstory-pink-a"
                            x1="157.424"
                            x2="106.436"
                            y1="222.393"
                            y2="8.123"
                            gradientUnits="userSpaceOnUse"
                        />
                    </defs>
                    <circle
                        cx="135.957"
                        cy="121.723"
                        r="100"
                        style="fill: url(#topstory-pink-b); fill-opacity: 1"
                        transform="rotate(13.446 210.12 -41.656)"
                    />
                </svg>

                <StoryblokImage
                    :media="props.blok.image"
                    type="mainPicture"
                    :alt="props.blok.image.alt ? props.blok.image.alt : ''"
                />
            </div>

            <div
                ref="textWrapperRef"
                class="text-wrapper"
            >
                <TextContentBlock
                    :headline="props.blok.headline"
                    :subheadline="props.blok.subheadline"
                    :text="props.blok.text"
                    :cta="props.blok.button"
                />
            </div>
        </div>

        <div class="background">
            <div
                ref="GradientShapeRef2"
                class="parallax-wrapper gradient-orange"
            >
                <GradientShape
                    shape="circle"
                    theme="pta-secondary"
                    gradient-angle="0deg"
                    gradient-start="0%"
                    gradient-end="130%"
                />
            </div>

            <div
                ref="GradientShapeRef1"
                class="parallax-wrapper gradient-pink"
            >
                <GradientShape
                    shape="circle"
                    theme="pta-primary"
                    gradient-angle="0deg"
                    gradient-start="0%"
                    gradient-end="120%"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted } from 'vue';
    import { gsap } from 'gsap';
    import { ScrollTrigger } from 'gsap/ScrollTrigger';

    const props = defineProps<{ blok: FamilyTopStoryStoryblok }>();

    gsap.registerPlugin(ScrollTrigger);

    const parallaxWrapperRef = ref();
    const textWrapperRef = ref();
    const imageWrapperRef = ref();
    const GradientShapeRef1 = ref();
    const GradientShapeRef2 = ref();

    const mediaMatch = gsap.matchMedia();

    const endPointValue = 'bottom bottom';

    onMounted(() => {
        mediaMatch.add('(max-width: 1023px)', () => {
            const dynEnd = () => `+=${window.innerHeight * 1.6}`;

            gsap.from(GradientShapeRef1.value, {
                scrollTrigger: {
                    trigger: imageWrapperRef.value,
                    scrub: true,
                    start: '40% bottom',
                    end: dynEnd,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 500,
                x: -50
            });

            gsap.from(GradientShapeRef2.value, {
                scrollTrigger: {
                    trigger: imageWrapperRef.value,
                    scrub: true,
                    start: '40% bottom',
                    end: dynEnd,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 350,
                x: -80
            });

            gsap.from(textWrapperRef.value, {
                scrollTrigger: {
                    trigger: textWrapperRef.value,
                    scrub: true,
                    start: 'start bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: -80
            });

            gsap.from(imageWrapperRef.value, {
                scrollTrigger: {
                    trigger: imageWrapperRef.value,
                    scrub: true,
                    start: '5% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: -80
            });
        });

        mediaMatch.add('(min-width: 1024px)', () => {
            gsap.from(GradientShapeRef1.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 1200,
                x: 50
            });

            gsap.from(GradientShapeRef2.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 600,
                x: -80
            });

            gsap.from(textWrapperRef.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: -80
            });

            gsap.from(imageWrapperRef.value, {
                scrollTrigger: {
                    trigger: parallaxWrapperRef.value,
                    scrub: 0.8,
                    start: '40% bottom',
                    end: endPointValue,
                    toggleActions: 'restart pause reverse pause'
                },
                y: 50
            });
        });
    });
</script>

<style lang="scss" scoped>
    @import '~/assets/scss/pta-family/pta-top-story.scss';
</style>
